import { mockResponse, request, isMockMode } from './client.js';
import { ORDERS, SAMPLE_TIMELINE } from '../data/orders.js';

/**
 * 訂單 API（需登入）。REACT_APP_USE_MOCK_ORDERS=false 走真實後端。
 *
 * OrderDto：{ id, status, statusLabel, subtotal, shipping, total,
 *   recipientName, recipientPhone, recipientAddress,
 *   shippingMethod(+Label), contactMethod(+Label), contactInfo, createdAt,
 *   items:[{ itemType, productId, variantId, sku, name, spec, unitPrice, qty }],
 *   timeline:[{ status, label, createdAt }] }   ← 列表為 null，詳情才帶
 *
 * 下單 payload 只需 items[{type, variantId, qty}] + 收件欄位；
 * 金額一律由後端重算（多帶的 name/price 欄位後端忽略，mock 顯示用）。
 */

let _store = [...ORDERS];

// 運費規則（顯示用；實際金額以後端 OrderService 重算為準，改規則要兩邊同步）
export const FREE_SHIPPING_THRESHOLD = 1000;
export const SHIPPING_FEE = 60;

// 前台
export function getOrders() {
  if (!isMockMode('orders')) return request('/api/orders');
  return mockResponse(_store);
}

export function getOrder(id) {
  if (!isMockMode('orders')) return request(`/api/orders/${id}`);
  return mockResponse(() => {
    const order = _store.find(o => o.id === id) || _store[0];
    return { ...order, timeline: SAMPLE_TIMELINE };
  });
}

export function createOrder(payload) {
  if (!isMockMode('orders')) return request('/api/orders', { method: 'POST', body: payload });
  return mockResponse(() => {
    // 模擬後端：金額重算、pending 起始、timeline 第一筆
    const subtotal = (payload.items || []).reduce((s, i) => s + (i.price || 0) * (i.qty || 1), 0);
    const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
    const newOrder = {
      id: `MD-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(Math.random() * 9999).toString().padStart(4, '0')}`,
      createdAt: new Date().toISOString(),
      status: 'pending',
      statusLabel: '待客服確認',
      subtotal,
      shipping,
      total: subtotal + shipping,
      recipientName: payload.recipientName,
      recipientPhone: payload.recipientPhone,
      recipientAddress: payload.recipientAddress,
      shippingMethod: payload.shippingMethod,
      contactMethod: payload.contactMethod,
      contactInfo: payload.contactInfo,
      items: (payload.items || []).map(i => ({
        itemType: i.type || 'physical', variantId: i.variantId,
        name: i.name, spec: (i.tags || []).join(' · ') || null,
        unitPrice: i.price || 0, qty: i.qty,
      })),
      timeline: [{ status: 'pending', label: '訂單已建立', createdAt: new Date().toISOString() }],
    };
    _store = [newOrder, ..._store];
    return newOrder;
  });
}

// 後台
export function adminGetOrder(id) {
  if (!isMockMode('orders')) return request(`/api/admin/orders/${id}`);
  return mockResponse(() => {
    const order = _store.find(o => o.id === id);
    if (!order) throw new Error(`找不到訂單：${id}`);
    return { ...order, timeline: order.timeline || SAMPLE_TIMELINE };
  });
}

export function adminGetOrders(params = {}) {
  if (!isMockMode('orders')) {
    // 逐參數塞，跳過 undefined/null/空值（URLSearchParams 會把 undefined 字串化成 "undefined"）
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const s = qs.toString();
    return request(`/api/admin/orders${s ? '?' + s : ''}`);
  }
  return mockResponse(() => {
    const page = params.page || 1;
    const limit = params.limit || 10;
    let result = [..._store];
    if (params.status && params.status !== 'all') {
      result = result.filter(o => o.status === params.status);
    }
    if (params.q || params.search) {
      const kw = String(params.q || params.search).toLowerCase();
      result = result.filter(o => o.id.toLowerCase().includes(kw));
    }
    const [sf, sd] = (params.sort || '').split(',');
    if (sf) {
      result.sort((a, b) => {
        const r = String(a[sf] ?? '').localeCompare(String(b[sf] ?? ''), undefined, { numeric: true });
        return sd === 'desc' ? -r : r;
      });
    }
    const start = (page - 1) * limit;
    return {
      items: result.slice(start, start + limit),
      total: result.length,
      page,
      limit,
      totalPages: Math.ceil(result.length / limit),
    };
  });
}

export function adminUpdateOrder(id, patch) {
  if (!isMockMode('orders')) return request(`/api/admin/orders/${id}`, { method: 'PATCH', body: patch });
  return mockResponse(() => {
    const idx = _store.findIndex(o => o.id === id);
    if (idx === -1) throw new Error(`找不到訂單：${id}`);
    const updated = { ..._store[idx], ...patch };
    _store = [..._store.slice(0, idx), updated, ..._store.slice(idx + 1)];
    return updated;
  });
}
